import {
  Input,
  Flex,
  Progress,
  Heading,
  useDisclosure,
  FormControl,
  FormLabel,
} from "@chakra-ui/react"
import { headingTheme } from "../../theme/components"
import { useEffect, useRef, useState } from "react"
import { useDebouncedCallback } from "use-debounce"
import mapsService from "../../services/maps.service"
import JGoogleIcon from "../JGoogleIcon/JGoogleIcon"
import { MapsAutocompleteProps, MapsUsageSource } from "../../types/maps.type"

export interface AutocompletePlacesResultProps {
  address: string
  longitude: number
  latitude: number
}

interface AutocompletePlacesProps {
  label?: string
  placeholder?: string
  onSelect?: (result: AutocompletePlacesResultProps) => void
  value?: string
  usageSource: MapsUsageSource
}

const MIN_SEARCH_LENGTH = 3
const SEARCH_DEBOUNCE_MS = 1500

function normalizeQuery(text: string) {
  return text.trim().replace(/\s+/g, ' ').toLowerCase()
}

export default function AutocompletePlaces(props: AutocompletePlacesProps) {
  const { label, placeholder, onSelect, value, usageSource } = props

  const [defaultValue, setDefaultValue] = useState(value??'')
  const [search, setSearch] = useState(defaultValue)
  const [suggestions, setSuggestions] = useState<MapsAutocompleteProps[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isGeocoding, setIsGeocoding] = useState(false)

  const isFocusDisclosure = useDisclosure()

  const lastQueryRef = useRef('')
  const requestIdRef = useRef(0)
  const abortControllerRef = useRef<AbortController | null>(null)
  const isGeocodingRef = useRef(false)

  // Invalidate any in-flight autocomplete so its response is ignored
  function cancelAutocomplete() {
    debouncedRunSearch.cancel()
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    requestIdRef.current += 1
    setIsLoading(false)
  }

  function clearSuggestions() {
    cancelAutocomplete()
    lastQueryRef.current = ''
    setSuggestions([])
  }

  async function runSearch(text: string) {
    const query = normalizeQuery(text)
    if (query.length < MIN_SEARCH_LENGTH || query === lastQueryRef.current) return

    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller
    const requestId = ++requestIdRef.current
    lastQueryRef.current = query
    setIsLoading(true)

    try {
      const resp = await mapsService.autocomplete({ search: text.trim(), usage_source: usageSource }, controller.signal)
      if (requestId !== requestIdRef.current) return
      setSuggestions(resp?.data?.suggestions ?? [])
    } catch {
      if (requestId !== requestIdRef.current) return
      // Let the user retry deliberately by editing the text
      lastQueryRef.current = ''
      setSuggestions([])
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false)
    }
  }

  const debouncedRunSearch = useDebouncedCallback(runSearch, SEARCH_DEBOUNCE_MS)

  function handleChange(text: string) {
    isFocusDisclosure.onOpen()
    setSearch(text)

    if (text.trim().length < MIN_SEARCH_LENGTH) return clearSuggestions()
    debouncedRunSearch(text)
  }

  function handleBlur() {
    isFocusDisclosure.onClose()
    clearSuggestions()
    setSearch(defaultValue)
  }

  async function handleSelect(suggestion: MapsAutocompleteProps, address?: string) {
    if (isGeocodingRef.current) return
    isGeocodingRef.current = true
    setIsGeocoding(true)

    try {
      const resp = await mapsService.geocode(
        suggestion.place_id
          ? { place_id: suggestion.place_id, usage_source: usageSource }
          : { address: suggestion.description, usage_source: usageSource }
      )

      setDefaultValue(address || suggestion.description)
      setSearch(address || suggestion.description)
      isFocusDisclosure.onClose()
      clearSuggestions()

      const addressComponents = resp?.data?.top_result?.address_components ?? []
      const maxAddressComponent = addressComponents?.length ? addressComponents[0] : undefined
      const location = resp?.data?.top_result?.location

      if (onSelect && maxAddressComponent && location) {
        onSelect({
          address: address || resp?.data?.top_result?.formatted_address,
          longitude: location.lng,
          latitude: location.lat
        })
      }
    } catch {
      // No automatic retry — suggestions stay available for the user to pick again
    } finally {
      isGeocodingRef.current = false
      setIsGeocoding(false)
    }
  }

  // Programmatic value changes only sync the text, they never trigger a search
  useEffect(() => {
    setSearch(value||'')
    setDefaultValue(value||'')
  }, [value])

  useEffect(() => {
    return () => {
      debouncedRunSearch.cancel()
      abortControllerRef.current?.abort()
    }
  }, [])

  return (
    <Flex
      direction='column'
      gap='8px'
    >
      <FormControl>
        {label
          ? <FormLabel {...headingTheme.sizes.chakra_title_small}>{label}</FormLabel>
          : null
        }
        <Input
          {...headingTheme.sizes.chakra_label_large}
          placeholder={placeholder??''}
          type="search"
          cursor="pointer"
          value={search}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              if (isLoading || isGeocoding) return
              if (suggestions.length <= 0) return
              handleSelect(suggestions[0], search)
            }
          }}
          onFocus={isFocusDisclosure.onOpen}
          onBlur={handleBlur}
        />
      </FormControl>

      {isLoading || isGeocoding
        ? <Progress size='xs' isIndeterminate />
        : null
      }

      {suggestions.length > 0 && isFocusDisclosure.isOpen
        ? <Flex
            direction='column'
            maxHeight='116px'
            overflowY='auto'
            pointerEvents={isGeocoding ? 'none' : 'auto'}
            opacity={isGeocoding ? 0.5 : 1}
          >
            {suggestions.map((suggest, suggestIndex) => {
              return (
                <Flex
                  key={suggest.place_id || suggestIndex}
                  paddingY='8px'
                  borderBottomWidth='1px'
                  borderBottomColor='surfacePlus3'
                  gap='4px'
                  cursor='pointer'
                  onMouseDown={(e) => {
                    e.preventDefault()
                    handleSelect(suggest)
                  }}

                >
                  <JGoogleIcon name="location_on" color='primary' fontSize='24px'  />
                  <Heading size='chakra_label_large' marginTop='2px'>{suggest.description}</Heading>
                </Flex>
              )
            }) }
          </Flex>
        : null
      }
    </Flex>
  )
}
